/**
 * PricingListPage — Liste des tarifs courants (admin/pricing).
 *
 * Route : /ops/pricing
 *
 * - Header gradient + bouton « Publier un tarif ».
 * - Filtre par provider.
 * - Table : provider / tier / unité / prix / date / description / actions.
 * - Actions : voir l'historique, déprécier (DELETE).
 *
 * Accès : ROLE_PLATFORM_ADMIN.
 */

import { useCustom, useDelete } from '@refinedev/core';
import { App as AntdApp, Button, Result, Select, Spin } from 'antd';
import {
  DollarSign,
  Filter,
  Plus,
  RefreshCw,
  Tag as TagIcon,
  History as HistoryIcon,
} from 'lucide-react';
import { useMemo, useState, type FC } from 'react';
import { useNavigate } from 'react-router-dom';
import ProviderPricingTable from '@/components/pricing/ProviderPricingTable';
import type { PricingProvider, PricingRow } from './types';
import { PROVIDER_LABELS, PRICING_PROVIDERS } from './types';

const PROVIDER_OPTIONS = PRICING_PROVIDERS.map((p) => ({
  value: p,
  label: PROVIDER_LABELS[p],
}));

const PricingListPage: FC = () => {
  const navigate = useNavigate();
  const { modal } = AntdApp.useApp();
  const [providerFilter, setProviderFilter] = useState<PricingProvider | null>(null);

  const { data, isLoading, isError, refetch, isFetching } = useCustom<{
    success: boolean;
    data: PricingRow[];
  }>({
    url: '/api/v1/admin/pricing',
    method: 'get',
    config: providerFilter ? { query: { provider: providerFilter } } : undefined,
  });

  const rows: PricingRow[] = useMemo(() => data?.data?.data ?? [], [data]);

  const { mutate: deletePricing, isLoading: deleting } = useDelete();

  const handleShowHistory = (row: PricingRow) => {
    const params = new URLSearchParams({
      provider: row.provider,
      tier: row.tier,
      unitType: row.unitType,
    });
    navigate(`/ops/pricing/history?${params.toString()}`);
  };

  const handleSupersede = (row: PricingRow) => {
    modal.confirm({
      title: 'Déprécier cette tarification ?',
      content: (
        <div className="space-y-2">
          <p className="text-sm">
            Provider : <strong>{row.providerLabel}</strong>
          </p>
          <p className="text-sm">
            Tier : <strong>{row.tierLabel}</strong> — Unité :{' '}
            <strong>{row.unitTypeLabel}</strong>
          </p>
          <p className="text-xs text-slate-500">
            Cette action clôture la ligne (effective_to = now), aucun remplaçant
            n'est créé automatiquement. L'historique reste consultable.
          </p>
        </div>
      ),
      okText: 'Déprécier',
      okButtonProps: { danger: true },
      cancelText: 'Annuler',
      onOk: () =>
        new Promise<void>((resolve, reject) => {
          deletePricing(
            {
              resource: 'admin/pricing',
              id: row.id,
            },
            {
              onSuccess: () => {
                void refetch();
                resolve();
              },
              onError: (err) => reject(err),
            },
          );
        }),
    });
  };

  const stats = useMemo(() => {
    const byProvider = new Map<string, number>();
    rows.forEach((r) => {
      byProvider.set(r.provider, (byProvider.get(r.provider) ?? 0) + 1);
    });
    return {
      total: rows.length,
      providers: byProvider.size,
    };
  }, [rows]);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="fc-gradient-header flex items-center justify-between rounded-2xl p-6 text-white">
        <div className="flex items-center gap-3">
          <DollarSign className="h-6 w-6" />
          <div>
            <h1 className="text-xl font-bold">Pricing (plateforme)</h1>
            <p className="mt-1 text-sm text-blue-100">
              Gestion des tarifs FO + coûts AI providers · Platform Admin
            </p>
          </div>
        </div>
        <Button
          type="primary"
          size="large"
          icon={<Plus className="h-4 w-4" />}
          onClick={() => navigate('/ops/pricing/new')}
          ghost
        >
          Publier un tarif
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        <div className="fc-card p-5">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs font-medium uppercase tracking-wider text-slate-500 dark:text-slate-400">
                Tarifs actifs
              </p>
              <p className="mt-1 text-2xl font-bold text-slate-900 dark:text-white">
                {stats.total}
              </p>
            </div>
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-blue-50 text-blue-600 dark:bg-blue-900/30 dark:text-blue-400">
              <DollarSign className="h-5 w-5" />
            </div>
          </div>
        </div>
        <div className="fc-card p-5">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs font-medium uppercase tracking-wider text-slate-500 dark:text-slate-400">
                Providers suivis
              </p>
              <p className="mt-1 text-2xl font-bold text-slate-900 dark:text-white">
                {stats.providers}
              </p>
            </div>
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-violet-50 text-violet-600 dark:bg-violet-900/30 dark:text-violet-400">
              <TagIcon className="h-5 w-5" />
            </div>
          </div>
        </div>
        <button
          type="button"
          onClick={() => navigate('/ops/pricing/simulator')}
          className="fc-card cursor-pointer p-5 text-left transition-shadow hover:shadow-md"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs font-medium uppercase tracking-wider text-slate-500 dark:text-slate-400">
                Simulateur
              </p>
              <p className="mt-1 text-base font-semibold text-slate-900 dark:text-white">
                Lancer une simulation
              </p>
              <p className="mt-0.5 text-xs text-slate-500 dark:text-slate-400">
                CA · coût upstream · marge
              </p>
            </div>
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-emerald-50 text-emerald-600 dark:bg-emerald-900/30 dark:text-emerald-400">
              <HistoryIcon className="h-5 w-5" />
            </div>
          </div>
        </button>
      </div>

      {/* Filtres */}
      <div className="fc-card flex flex-wrap items-center gap-3 p-4">
        <Filter className="h-4 w-4 text-slate-400" />
        <Select<PricingProvider | undefined>
          allowClear
          placeholder="Filtrer par provider"
          options={PROVIDER_OPTIONS}
          value={providerFilter ?? undefined}
          onChange={(v) => setProviderFilter(v ?? null)}
          style={{ minWidth: 260 }}
          size="middle"
        />
        <Button
          icon={<RefreshCw className="h-4 w-4" />}
          onClick={() => void refetch()}
          loading={isFetching && !isLoading}
        >
          Actualiser
        </Button>
      </div>

      {/* Content */}
      {isLoading ? (
        <div className="fc-card flex items-center justify-center p-10">
          <Spin size="large" />
        </div>
      ) : isError ? (
        <Result
          status="error"
          title="Impossible de charger les tarifs"
          subTitle="Vérifier l'endpoint GET /api/v1/admin/pricing et le token Platform Admin."
          extra={
            <Button
              type="primary"
              icon={<RefreshCw className="h-4 w-4" />}
              onClick={() => void refetch()}
            >
              Réessayer
            </Button>
          }
        />
      ) : (
        <div className="fc-card overflow-hidden p-0">
          <ProviderPricingTable
            rows={rows}
            loading={deleting}
            onShowHistory={handleShowHistory}
            onSupersede={handleSupersede}
          />
        </div>
      )}
    </div>
  );
};

export default PricingListPage;
